// Page Emploi Mobile - Design unifié
// Header simple "Emplois" + Onglets Tous/CDI/Intérim + Loupe recherche fullscreen

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, Clock, Briefcase, MapPin, Euro, Building2, Loader2,
  RefreshCw, SlidersHorizontal, Zap, Laptop, BookmarkPlus, Bookmark
} from 'lucide-react';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import UnifiedFilterSystem from '../../components/filters/UnifiedFilterSystem';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de contrat
const JOB_TYPES = [
  { id: 'cdi', label: 'CDI', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'cdd', label: 'CDD', color: 'bg-blue-100 text-blue-700' },
  { id: 'interim', label: 'Intérim', color: 'bg-orange-100 text-orange-700' },
  { id: 'apprentissage', label: 'Apprentissage', color: 'bg-purple-100 text-purple-700' },
];

// Mock data
const MOCK_JOBS = [
  { id: 'job-1', title: 'Électricien qualifié H/F', company: 'Elec Pro Services', city: 'Paris', job_type: 'cdi', salary_min: 2200, salary_max: 2800, is_urgent: true, created_at: new Date(Date.now() - 1 * 24 * 3600000).toISOString() },
  { id: 'job-2', title: 'Plombier chauffagiste', company: 'Chauff\'Confort', city: 'Lyon', job_type: 'cdi', salary_min: 2400, salary_max: 3200, is_urgent: false, created_at: new Date(Date.now() - 2 * 24 * 3600000).toISOString() },
  { id: 'job-3', title: 'Maçon coffreur bancheur', company: 'BTP Construction Sud', city: 'Marseille', job_type: 'cdi', salary_min: 2000, salary_max: 2600, is_urgent: true, created_at: new Date(Date.now() - 3 * 24 * 3600000).toISOString() },
  { id: 'job-4', title: 'Peintre en bâtiment', company: 'Déco Habitat', city: 'Bordeaux', job_type: 'cdd', salary_min: 1900, salary_max: 2300, is_urgent: false, created_at: new Date(Date.now() - 5 * 24 * 3600000).toISOString() },
  { id: 'job-5', title: 'Menuisier poseur ALU/PVC', company: 'Fenêtres Plus', city: 'Nantes', job_type: 'cdi', salary_min: 2000, salary_max: 2500, is_urgent: true, created_at: new Date(Date.now() - 4 * 24 * 3600000).toISOString() },
  { id: 'job-6', title: 'Façadier ITE', company: 'Iso Façades', city: 'Lyon', job_type: 'interim', salary_min: 14, salary_max: 18, salary_type: 'horaire', is_urgent: true, created_at: new Date(Date.now() - 1 * 24 * 3600000).toISOString() },
];

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Hier";
  if (diff < 7) return `Il y a ${diff}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// Format salaire
const formatSalary = (job) => {
  if (!job.salary_min && !job.salary_max) return null;
  const suffix = job.salary_type === 'horaire' ? '/h' : '/mois';
  if (job.salary_min && job.salary_max) {
    return `${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()} €${suffix}`;
  }
  return `${(job.salary_min || job.salary_max).toLocaleString()} €${suffix}`;
};

// Carte Job
function JobCard({ job, isSaved, onSave }) {
  const navigate = useNavigate();
  const jobType = JOB_TYPES.find(t => t.id === job.job_type) || JOB_TYPES[0];
  const salary = formatSalary(job);
  const isNew = job.created_at && (new Date() - new Date(job.created_at)) < 3 * 24 * 60 * 60 * 1000;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm"
      onClick={() => navigate(`/emploi/${job.id}`)}
    >
      {/* Badges */}
      <div className="flex items-center gap-2 mb-2">
        {job.is_urgent && (
          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded flex items-center gap-1">
            <Zap size={10} /> URGENT
          </span>
        )}
        {isNew && (
          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">NOUVEAU</span>
        )}
        {job.remote_possible && (
          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-medium rounded flex items-center gap-1">
            <Laptop size={10} /> Remote
          </span>
        )}
      </div>

      {/* Titre */}
      <h3 className="font-bold text-base text-gray-900 mb-1 line-clamp-2">{job.title}</h3>

      {/* Entreprise */}
      <p className="text-sm text-gray-700 font-medium mb-1">{job.company}</p>

      {/* Localisation */}
      <p className="text-xs text-gray-500 flex items-center gap-1 mb-2">
        <MapPin size={12} /> {job.city}
      </p>

      {/* Salaire */}
      {salary && (
        <div className="mb-2">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg font-semibold text-xs">
            <Euro size={12} /> {salary}
          </span>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${jobType.color}`}>{jobType.label}</span>
          <span className="text-[10px] text-gray-400">{formatDate(job.created_at)}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onSave(job); }}
          className="p-1.5 hover:bg-gray-100 rounded-full"
        >
          {isSaved ? <Bookmark size={16} className="text-[#2557a7] fill-[#2557a7]" /> : <BookmarkPlus size={16} className="text-gray-400" />}
        </button>
      </div>
    </motion.div>
  );
}

export default function MobileEmploiPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savedJobs, setSavedJobs] = useState([]);
  
  // Filtres
  const [activeTab, setActiveTab] = useState(searchParams.get('type') || 'tous');
  const [showFullscreenSearch, setShowFullscreenSearch] = useState(false);
  const [showFullFilters, setShowFullFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const searchInputRef = useRef(null);
  
  useEffect(() => {
    fetchJobs();
    const saved = localStorage.getItem('savedJobs');
    if (saved) setSavedJobs(JSON.parse(saved));
  }, [activeTab]);
  
  useEffect(() => {
    try {
      const saved = localStorage.getItem('emploi_recent_searches');
      if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
    } catch {}
  }, []);
  
  useEffect(() => {
    if (showFullscreenSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 250);
    }
  }, [showFullscreenSearch]);
  
  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      if (activeTab !== 'tous') params.set('job_type', activeTab);
      params.set('limit', '20');
      
      const res = await fetch(`${API_URL}/api/emploi/offres?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setJobs(data.offres || []);
      } else {
        // Fallback mock data
        let filtered = MOCK_JOBS;
        if (activeTab !== 'tous') {
          filtered = MOCK_JOBS.filter(j => j.job_type === activeTab);
        }
        setJobs(filtered);
      }
    } catch (e) {
      setJobs(MOCK_JOBS.filter(j => activeTab === 'tous' || j.job_type === activeTab));
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchJobs();
    setRefreshing(false);
  };
  
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    const trimmed = query.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    try { localStorage.setItem('emploi_recent_searches', JSON.stringify(updated)); } catch {}
  };
  
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('emploi_recent_searches'); } catch {}
  };
  
  const handleSearchSubmit = (query) => {
    if (query && query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query.trim());
    }
    setShowFullscreenSearch(false);
  };
  
  const handleSaveJob = (job) => {
    const isSaved = savedJobs.includes(job.id);
    let newSaved;
    if (isSaved) {
      newSaved = savedJobs.filter(id => id !== job.id);
      toast.success('Offre retirée');
    } else {
      newSaved = [...savedJobs, job.id];
      toast.success('Offre sauvegardée');
    }
    setSavedJobs(newSaved);
    localStorage.setItem('savedJobs', JSON.stringify(newSaved));
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    if (tab !== 'tous') params.set('type', tab);
    else params.delete('type');
    setSearchParams(params, { replace: true });
  };
  
  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingBottom: '80px' }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-[36px] z-50">
        <div className="flex items-center justify-between h-14 px-3">
          <div className="flex items-center gap-0.5">
            <h1 className="text-lg font-bold text-[#1E3A5F]">Emplois</h1>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={handleRefresh} disabled={refreshing} className="p-2 hover:bg-gray-100 rounded-full">
              <RefreshCw size={20} className={`text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setShowFullscreenSearch(true)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="emploi-search-btn">
              <Search size={20} className="text-gray-600" />
            </button>
          </div>
        </div>
        
        {/* Onglets */}
        <div className="flex border-b border-gray-100">
          <button onClick={() => handleTabChange('tous')} className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${activeTab === 'tous' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}>
            <Briefcase size={14} /> Tous
          </button>
          <button onClick={() => handleTabChange('cdi')} className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${activeTab === 'cdi' ? 'text-emerald-600 border-b-2 border-emerald-500' : 'text-gray-500'}`}>
            CDI
          </button>
          <button onClick={() => handleTabChange('interim')} className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${activeTab === 'interim' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}>
            Intérim
          </button>
        </div>
      </header>
      
      {/* Contenu */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-500">{jobs.length} offre{jobs.length > 1 ? 's' : ''}</p>
          {searchQuery && (
            <button onClick={() => { setSearchQuery(''); fetchJobs(); }} className="text-xs text-orange-600 font-medium">Réinitialiser</button>
          )}
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Briefcase size={32} className="text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Aucune offre trouvée</h3>
            <p className="text-sm text-gray-500">{searchQuery ? `Aucun résultat pour "${searchQuery}"` : 'Modifie tes critères'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map(job => (
              <JobCard key={job.id} job={job} isSaved={savedJobs.includes(job.id)} onSave={handleSaveJob} />
            ))}
          </div>
        )}
      </div>
      
      {/* Recherche Fullscreen */}
      <AnimatePresence>
        {showFullscreenSearch && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#ffffff', zIndex: 99999 }}>
            <div className="flex items-center justify-end" style={{ padding: '8px 12px', paddingTop: 'max(8px, env(safe-area-inset-top, 8px))' }}>
              <button onClick={() => setShowFullscreenSearch(false)} style={{ width: '32px', height: '32px' }}>
                <X size={24} className="text-[#64748B]" />
              </button>
            </div>
            
            <div className="px-3 pb-3">
              <form onSubmit={(e) => { e.preventDefault(); handleSearchSubmit(searchQuery); }} className="flex items-center w-full" style={{ height: '56px', border: '2px solid #E8650A', borderRadius: '28px', background: '#F8F9FA', overflow: 'hidden' }}>
                <motion.button type="submit" initial={{ x: 320, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 280, damping: 24, delay: 0.03 }} className="flex items-center justify-center flex-shrink-0" style={{ width: '56px', height: '56px', background: '#E8650A', borderRadius: '26px 0 0 26px', position: 'relative', left: '-2px' }}>
                  <Search size={24} className="text-white" />
                </motion.button>
                <input ref={searchInputRef} type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Métier, entreprise, ville..." className="flex-1 bg-transparent text-[#333] placeholder-[#999] focus:outline-none" style={{ fontSize: '16px', paddingLeft: '12px', paddingRight: '16px' }} autoFocus />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="flex items-center justify-center mr-4" style={{ width: '22px', height: '22px', background: '#C9CDD4', borderRadius: '50%' }}>
                    <X size={14} className="text-white" />
                  </button>
                )}
              </form>
            </div>
            
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="px-4 py-4">
              <button onClick={() => { setShowFullscreenSearch(false); setTimeout(() => setShowFullFilters(true), 150); }} className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-semibold text-white" style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)', boxShadow: '0 4px 14px rgba(30, 58, 95, 0.25)' }}>
                <SlidersHorizontal size={20} />
                <span style={{ fontSize: '15px' }}>Trouve ton emploi en 3 clics</span>
              </button>
            </motion.div>
            
            <div className="p-4 bg-white flex-1 overflow-y-auto">
              <p className="text-[11px] text-[#E8650A] uppercase font-semibold tracking-wide mb-4">Recherche dans : Emplois</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold">Recherches récentes</p>
                  {recentSearches.length > 0 && <button onClick={clearRecentSearches} className="text-[11px] text-[#E8650A] font-medium">Effacer</button>}
                </div>
                {recentSearches.length > 0 ? (
                  <div className="space-y-1">
                    {recentSearches.map((search, idx) => (
                      <button key={idx} onClick={() => handleSearchSubmit(search)} className="w-full flex items-center gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg">
                        <Clock size={16} className="text-[#9CA3AF]" />
                        <span className="text-[14px] text-[#374151]">{search}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Search size={32} className="text-[#E5E7EB] mb-3" />
                    <p className="text-[#9CA3AF] text-sm">Aucune recherche récente</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* UnifiedFilterSystem */}
      <UnifiedFilterSystem context="emploi" isOpen={showFullFilters} onClose={() => setShowFullFilters(false)} filters={activeFilters} onFiltersChange={setActiveFilters} resultsCount={jobs.length} onApply={() => setShowFullFilters(false)} />
      
      {/* MobileBottomNav est géré globalement par MobileNavWrapper dans App.js */}
    </div>
  );
}
